import React from 'react';
import { MapPin, Maximize2, Building2, Route, BadgeCheck } from 'lucide-react';

interface PlotSummary {
    gid: number;
    fp_no: string;
    fps_code: string;
    area_sqm: number;
    zone_code: string;
    base_fsi: number;
    max_fsi: number;
    estimated_buildable_sqm: number;
    location: string;
    tps: string;
    road_width_m: number;
    premium_applicable: boolean;
}

interface PlotSummaryCardProps {
    plot: PlotSummary;
    onStartChat: () => void;
    loading?: boolean;
}

const PlotSummaryCard: React.FC<PlotSummaryCardProps> = ({ plot, onStartChat, loading }) => {
    if (loading) {
        return (
            <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100 p-4 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/2 mb-3"></div>
                <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
            </div>
        );
    }

    const formatNumber = (num: number) => {
        return new Intl.NumberFormat('en-IN').format(Math.round(num));
    };

    return (
        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-3">
                <div className="flex items-center justify-between">
                    <h3 className="text-white font-semibold text-lg">Plot {plot.fp_no || plot.gid}</h3>
                    <span className="bg-white/20 text-white text-xs px-2 py-1 rounded-full font-medium">
                        {plot.zone_code}
                    </span>
                </div>
                <p className="text-emerald-100 text-sm flex items-center mt-1">
                    <MapPin className="w-3 h-3 mr-1" />
                    {plot.location}
                </p>
            </div>

            {/* Quick Stats */}
            <div className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center text-gray-500 text-xs mb-1">
                            <Maximize2 className="w-3 h-3 mr-1" />
                            Plot Area
                        </div>
                        <div className="font-semibold text-gray-900">
                            {formatNumber(plot.area_sqm)} m²
                        </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center text-gray-500 text-xs mb-1">
                            <Building2 className="w-3 h-3 mr-1" />
                            Base FSI
                        </div>
                        <div className="font-semibold text-gray-900">{plot.base_fsi}</div>
                        <div className="text-xs text-gray-400">Max: {plot.max_fsi}</div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center text-gray-500 text-xs mb-1">
                            <Route className="w-3 h-3 mr-1" />
                            Road Width
                        </div>
                        <div className="font-semibold text-gray-900">{plot.road_width_m}m</div>
                        {plot.premium_applicable && (
                            <div className="text-xs text-emerald-600">✓ Premium eligible</div>
                        )}
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center text-gray-500 text-xs mb-1">
                            <BadgeCheck className="w-3 h-3 mr-1" />
                            Buildable
                        </div>
                        <div className="font-semibold text-gray-900">
                            {formatNumber(plot.estimated_buildable_sqm)} m²
                        </div>
                    </div>
                </div>

                {/* CTA Button */}
                <button
                    onClick={onStartChat}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                >
                    <span>Ask about this plot</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default PlotSummaryCard;
